<script setup lang="ts">
import { type Props } from './types';

const props = withDefaults(defineProps<Props>(), {
  text: '',
  to: '',
  type: '',
  big: false,
  small: false,
  white: false,
  gray: false,
  shadow: false,
  disabled: false,
  showIcon: false,
  smallHeight: false,
  iconWidth: '16',
  iconHeight: '16',
  lightOrange: false,
});

const component = computed(() => {
  if (props.to) {
    return resolveComponent('nuxt-link');
  }
  return 'button';
});

const emit = defineEmits(['click']);

const handleClick = (event: Event): void => {
  if (props.disabled) {
    event.preventDefault();
    return;
  }
  if (props.to) {
    emit('click');
    return;
  }

  event.preventDefault();
  emit('click');
};
</script>

<template>
  <component
    :is="component"
    :class="[
      'app-button',
      { '_light-orange': lightOrange },
      { '_white-bg': white },
      { '_gray-bg': gray },
      { _shadow: shadow },
      { _link: to },
      { '_sm-height': smallHeight },
      { _disabled: disabled },
    ]"
    :to="to || {}"
    :type="type || {}"
    :aria-label="text"
    @click="handleClick"
  >
    <slot name="icon-left" />
    {{ text }}
    <slot name="icon-right" />
  </component>
</template>

<style lang="less">
.app-button {
  .button-reset();

  display: inline-flex;
  justify-content: center;
  gap: @size-x2;
  align-items: center;
  vertical-align: top;
  background-color: @color-tango;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  line-height: 130%;
  width: 100%;
  border-radius: @size-x3;
  transition: all @A linear;
  height: 43px;
  padding: @size-x3 @size-x4;
  white-space: nowrap;

  &:hover:not(._white-bg, ._light-orange, ._gray-bg, ._disabled) {
    opacity: 0.75;
  }

  &._sm-height {
    height: 38px;
  }

  &._link {
    .a-reset();

    color: #fff;
  }

  &._light-orange {
    color: @color-tango;
    background-color: rgba(@color-tango, 0.1);

    &:hover {
      color: #fff;
      background-color: rgba(@color-tango, 0.75);
    }
  }

  &._white-bg {
    background-color: #fff;
    color: @color-cod-gray;

    &:hover {
      color: @color-tango;
    }
  }

  &._gray-bg {
    background-color: @color-athens-gray-dark;
    color: @color-cod-gray;

    &:hover {
      color: @color-tango;
    }
  }

  &._shadow {
    box-shadow: 0px @main-size @size-x2 0px #00000026;
  }

  &._disabled {
    background-color: @color-alto;
    color: #fff;
    cursor: default;
    pointer-events: none;
  }
}
</style>
